import { DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  input,
  isDevMode,
  output,
  untracked,
  viewChild
} from '@angular/core';
import { SimIconModule } from '@simOn/ui/sim-icon';
import { SimSpinnerComponent } from '@simOn/ui/sim-spinner';
import { EllipsisTooltipDirective } from '@simOn/ui/sim-tooltip';
import { AgentActionType, AgentChatAction, AgentChatMessage, AgentMessageRole } from '../../model';
import { AgentMarkdownPipe } from '../../pipes';

/** Icon tile of a card: the type of the item it opens or prepares. */
const ACTION_ICONS: Record<AgentActionType, string> = {
  [AgentActionType.Unknown]: 'chat',
  [AgentActionType.NavigateToAsset]: 'asset',
  [AgentActionType.OpenDocument]: 'description',
  [AgentActionType.NavigateToTicket]: 'task',
  [AgentActionType.NavigateToEvent]: 'calendar',
  [AgentActionType.CreateAsset]: 'asset',
  [AgentActionType.UpdateAsset]: 'asset',
  [AgentActionType.CreateTicket]: 'task',
  [AgentActionType.UpdateTicket]: 'task',
  [AgentActionType.UpdateTicketStatus]: 'task',
  [AgentActionType.CreateEvent]: 'calendar',
  [AgentActionType.UpdateEvent]: 'calendar'
};

@Component({
  selector: 'ai-agent-message-list',
  imports: [DatePipe, SimIconModule, SimSpinnerComponent, EllipsisTooltipDirective, AgentMarkdownPipe],
  templateUrl: './agent-message-list.component.html',
  styleUrl: './agent-message-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AgentMessageListComponent {
  readonly messages = input.required<readonly AgentChatMessage[]>();
  /** Name of the selected space, shown in the new chat greeting. */
  readonly spaceName = input<string>('');
  /** Note under the conversation when it has cards (out of tokens: the cards still work); `null` hides it. */
  readonly cardsHint = input<string | null>(null);
  /** History of the selected conversation is being fetched. */
  readonly loading = input<boolean>(false);
  /** A reply is pending; shows the "thinking" indicator under the last message. */
  readonly sending = input<boolean>(false);
  readonly retry = output<string>();
  /** The user picked an action button under an assistant message. */
  readonly action = output<AgentChatAction>();
  /** The user asked to move the 3D walk to the target of an action. */
  readonly goToPosition = output<AgentChatAction>();

  protected readonly AgentMessageRole = AgentMessageRole;
  /** Token usage is diagnostic data for now; it is only rendered in dev builds. */
  protected readonly showUsage = isDevMode();
  protected readonly retryLabel = $localize`:@@AI_AGENT_RETRY:Retry`;
  protected readonly removedLabel = $localize`:@@AI_AGENT_VALUE_REMOVED:removed`;
  private readonly _eyebrowTexts = {
    draft: $localize`:@@AI_AGENT_CARD_DRAFT:Draft`,
    saved: $localize`:@@AI_AGENT_CARD_SAVED:Saved`,
    statusDone: $localize`:@@AI_AGENT_STATUS_CHANGED_DONE:Status changed · Done`,
    oneChange: $localize`:@@AI_AGENT_ONE_CHANGE:1 change`,
    changes: (count: number) => $localize`:@@AI_AGENT_N_CHANGES:${count}:count: changes`
  };
  private readonly _reviewLabel = $localize`:@@AI_AGENT_OPEN_FORM:Open form to review & save`;
  private readonly _actionOpenLabels: Partial<Record<AgentActionType, string>> = {
    [AgentActionType.NavigateToAsset]: $localize`:@@AI_AGENT_OPEN_ASSET:Open asset`,
    [AgentActionType.OpenDocument]: $localize`:@@AI_AGENT_OPEN_DOCUMENT:Open document`,
    [AgentActionType.NavigateToTicket]: $localize`:@@AI_AGENT_OPEN_TASK:Open task`,
    [AgentActionType.NavigateToEvent]: $localize`:@@AI_AGENT_OPEN_EVENT_CARD:Open event`,
    [AgentActionType.UpdateTicketStatus]: $localize`:@@AI_AGENT_CHANGE_STATUS:Change status`
  };

  private readonly _scrollContainer = viewChild<ElementRef<HTMLElement>>('scrollContainer');

  constructor() {
    effect(() => {
      // Re-run when the list or the pending indicator changes, then scroll once the view is updated.
      this.messages();
      this.sending();
      untracked(() => setTimeout(() => this._scrollToBottom()));
    });
  }

  protected onRetry(message: AgentChatMessage): void {
    this.retry.emit(message.id);
  }

  protected onAction(action: AgentChatAction): void {
    this.action.emit(action);
  }

  protected onGoToPosition(action: AgentChatAction): void {
    this.goToPosition.emit(action);
  }

  protected actionIcon(action: AgentChatAction): string {
    return ACTION_ICONS[action.type];
  }

  protected actionOpenLabel(action: AgentChatAction): string {
    return this._actionOpenLabels[action.type] ?? this._reviewLabel;
  }

  /** "NEW ASSET · DRAFT", "EDIT TASK · 2 CHANGES", "STATUS CHANGED · DONE" (upper-cased in CSS). */
  protected eyebrow(action: AgentChatAction): string | null {
    const { typeName, kind, changes, completed } = action;
    if (!typeName) return null;
    if (completed)
      return kind === 'status' ? this._eyebrowTexts.statusDone : `${typeName} · ${this._eyebrowTexts.saved}`;
    if (kind === 'status') return typeName;
    if (!changes.length) return `${typeName} · ${this._eyebrowTexts.draft}`;
    const count = changes.length === 1 ? this._eyebrowTexts.oneChange : this._eyebrowTexts.changes(changes.length);
    return `${typeName} · ${count}`;
  }

  /** Room the possible duplicates of a new draft live in; `null` when the draft has no room. */
  protected duplicateRoom(action: AgentChatAction): string | null {
    return (action.dto as { roomName?: string | null }).roomName || null;
  }

  /** The reminder under the cards that a draft is only saved through its form. */
  protected readonly hasCards = computed(() => this.messages().some(({ actions }) => actions.length > 0));

  protected hasDraft(message: AgentChatMessage): boolean {
    return message.actions.some(({ kind }) => kind === 'draft');
  }

  private _scrollToBottom(): void {
    const element = this._scrollContainer()?.nativeElement;
    if (element) element.scrollTop = element.scrollHeight;
  }
}
